<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useReveal } from '../composables/useReveal'

const { t } = useI18n()
const { elementRef: heroRef, isVisible } = useReveal(0.1)
</script>

<template>
  <section ref="heroRef" class="hero" aria-labelledby="hero-title">
    <div class="hero__gradient" />
    <div class="hero__grid" />

    <div class="container hero__content">
      <div :class="['hero__badge', 'reveal', { visible: isVisible }]">
        <span />{{ t('hero.badge') }}
      </div>

      <h1
        id="hero-title"
        :class="['hero__title', 'reveal', 'reveal-delay-1', { visible: isVisible }]"
      >
        {{ t('hero.title') }}
        <span class="gradient-text">{{ t('hero.titleHighlight') }}</span>
      </h1>

      <p
        :class="['hero__subtitle', 'reveal', 'reveal-delay-2', { visible: isVisible }]"
      >
        {{ t('hero.subtitle') }}
      </p>

      <div
        :class="['hero__actions', 'reveal', 'reveal-delay-3', { visible: isVisible }]"
      >
        <a href="#contact" class="btn btn-accent">{{ t('hero.ctaPrimary') }}</a>
        <a href="#product" class="btn btn-secondary">{{ t('hero.ctaSecondary') }}</a>
      </div>

      <div
        :class="['hero__stats', 'reveal', 'reveal-delay-4', { visible: isVisible }]"
        role="list"
      >
        <div v-for="key in ['efficiency', 'accuracy', 'coverage']" :key="key" class="hero__stat">
          <div class="hero__stat-value">{{ t(`hero.stats.${key}Value`) }}</div>
          <div class="hero__stat-label">{{ t(`hero.stats.${key}`) }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 6rem;
  padding-bottom: 4rem;
  overflow: hidden;
  background: var(--color-bg);
}

.hero__gradient {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 80% 60% at 50% -10%, rgba(13, 115, 119, 0.12), transparent 60%),
    radial-gradient(ellipse 60% 50% at 80% 60%, rgba(196, 92, 62, 0.06), transparent 50%);
  pointer-events: none;
}

.hero__grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.03) 1px, transparent 1px);
  background-size: 80px 80px;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 40%, black, transparent 70%);
  -webkit-mask-image: radial-gradient(ellipse 70% 70% at 50% 40%, black, transparent 70%);
  pointer-events: none;
}

.hero__content {
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: 980px;
}

.hero__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  color: var(--color-text-secondary);
  margin-bottom: 2rem;
}

.hero__badge span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-accent);
  animation: pulse 2s var(--ease-in-out-sine) infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.hero__title {
  margin-bottom: 1.5rem;
  color: var(--color-dark);
}

.hero__title span {
  display: block;
}

.hero__subtitle {
  max-width: 720px;
  margin-inline: auto;
  margin-bottom: 2.5rem;
  font-size: clamp(1.0625rem, 1.5vw, 1.25rem);
  line-height: 1.75;
  color: var(--color-text-secondary);
}

.hero__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 5rem;
}

.hero__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 720px;
  margin-inline: auto;
  padding: 2rem;
  border-radius: var(--radius-lg);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.06);
}

.hero__stat-value {
  font-family: var(--font-display);
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: var(--color-dark);
  line-height: 1;
  margin-bottom: 0.5rem;
}

.hero__stat-label {
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  font-weight: 500;
}

@media (max-width: 640px) {
  .hero__stats {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  .hero__actions {
    flex-direction: column;
  }

  .hero__actions .btn {
    width: 100%;
  }
}
</style>
